import { useCallback, useState } from 'react'
import { useTranslation } from 'react-i18next'

import { Close12, Download16 } from '@makyoapp/makyo-icon/icon'
import { Button, ButtonVariant, ShapeVariant } from '@makyoapp/makyo-ui'

interface StoragePreviewPDFProps {
  fileUrl: string
  fileName: string
  onClose: () => void
}

export default function StoragePreviewPDF({ fileUrl, fileName, onClose }: StoragePreviewPDFProps) {
  const { t } = useTranslation()
  const [isLoading, setIsLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const handleDownload = useCallback(() => {
    const link = document.createElement('a')
    link.href = fileUrl
    link.download = fileName
    link.target = '_blank'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }, [fileUrl, fileName])

  return (
    <div className='flex h-full w-full flex-col bg-slate-900'>
      {/* Header */}
      <div className='flex items-center justify-between border-b border-slate-700 p-4'>
        <div className='flex items-center space-x-4'>
          <h2 className='text-lg font-semibold text-white truncate max-w-md' title={fileName}>
            {fileName}
          </h2>
        </div>

        <div className='flex items-center space-x-2 ml-4'>
          {/* Download Button */}
          <Button
            onClick={handleDownload}
            className='px-3 cursor-pointer'
            variant={ButtonVariant.Normal}
            icon={<Download16 className='w-4 h-4' />}
          >
            {t('Download')}
          </Button>

          <Button
            onClick={onClose}
            className='px-2 cursor-pointer'
            noPadding
            variant={ButtonVariant.Ghost}
            shape={ShapeVariant.Circle}
            icon={<Close12 className='w-4 h-4' />}
          />
        </div>
      </div>

      {/* PDF Viewer */}
      <div className='flex-1 relative overflow-hidden'>
        {isLoading && (
          <div className='absolute inset-0 flex items-center justify-center bg-slate-800'>
            <div className='text-center flex flex-col items-center'>
              <div className='loading spin !size-8 mb-4'></div>
              <p className='text-slate-400'>{t('Loading PDF...')}</p>
            </div>
          </div>
        )}

        {error && (
          <div className='inset-0 flex items-center justify-center bg-slate-800 min-h-20'>
            <div className='text-center'>
              <div className='text-red-400 mb-4'>
                <svg className='w-16 h-16 mx-auto' fill='currentColor' viewBox='0 0 20 20'>
                  <path
                    fillRule='evenodd'
                    d='M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z'
                    clipRule='evenodd'
                  />
                </svg>
              </div>
              <p className='text-slate-400 mb-4'>{error}</p>
              <Button onClick={() => window.open(fileUrl, '_blank')} variant={ButtonVariant.Normal}>
                {t('Open in New Tab')}
              </Button>
            </div>
          </div>
        )}

        {fileUrl && !error && (
          <div className='h-full overflow-auto bg-slate-800'>
            <div className='flex justify-center p-4'>
              <div className='bg-white shadow-lg'>
                <iframe
                  src={`${fileUrl}#toolbar=0&navpanes=0&scrollbar=0`}
                  className='w-full h-full border-0'
                  style={{ minHeight: '600px', width: '65vw' }}
                  onLoad={() => {
                    setIsLoading(false)
                    setError(null)
                  }}
                  onError={() => setError(t('Failed to load PDF document'))}
                />
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  )
}
